import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Modal, Btn, Field, TextInput, Textarea, Select, Icons, Badge } from "@/components/ui-lite";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X } from "lucide-react";

const emptyForm = {
  code: "", name: "", aggregator_id: "", default_ggr_pct: 15,
  game_overrides: [], communications: [], min_ggr_usd: 0, requires_db_verification: true,
  invoice_currency: "USD", payment_method: "USDT_TRC20", notes: "",
};

const COMM_BUCKETS = ["Tech Support", "Finance", "Marketing", "Commercial", "Escalation"];
const COMM_MODES = [
  { v: "email",    label: "Email" },
  { v: "slack",    label: "Slack" },
  { v: "skype",    label: "Skype" },
  { v: "telegram", label: "Telegram" },
  { v: "whatsapp", label: "WhatsApp" },
  { v: "jira",     label: "JIRA" },
  { v: "web",      label: "Web / Portal" },
  { v: "phone",    label: "Phone" },
  { v: "other",    label: "Other" },
];

export default function ProvidersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [aggs, setAggs] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/providers").then((r) => setRows(r.data));
    api.get("/aggregators").then((r) => setAggs(r.data));
    api.get("/currencies").then((r) => setCurrencies(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, aggregator_id: form.aggregator_id || null };
      if (editing) await api.put(`/providers/${editing.id}`, payload);
      else await api.post("/providers", payload);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete provider?")) return;
    await api.delete(`/providers/${id}`); load();
  };

  const addOverride = () => setForm({ ...form, game_overrides: [...form.game_overrides, { game: "", ggr_pct: 0 }] });
  const rmOverride = (i) => setForm({ ...form, game_overrides: form.game_overrides.filter((_, x) => x !== i) });
  const setOverride = (i, key, val) => {
    const arr = [...form.game_overrides]; arr[i] = { ...arr[i], [key]: val };
    setForm({ ...form, game_overrides: arr });
  };

  return (
    <AppShell
      title="Providers"
      subtitle="Game providers with per-provider and per-game GGR percentages"
      actions={isAdmin && (
        <Btn data-testid="provider-add" onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }}>
          <Icons.Plus size={16} className="inline mr-1"/> New provider
        </Btn>
      )}
    >
      <DataTable
        testid="providers-table"
        columns={[
          { key: "code", label: "Code", mono: true },
          { key: "name", label: "Name" },
          { key: "aggregator_id", label: "Aggregator", render: (r) => {
              const a = aggs.find((x) => x.id === r.aggregator_id);
              return a ? <Badge tone="info">{a.code}</Badge> : <span className="text-[var(--wv-text-3)]">—</span>;
            }},
          { key: "default_ggr_pct", label: "GGR %", mono: true, render: (r) => `${Number(r.default_ggr_pct).toFixed(2)}%` },
          { key: "min_ggr_usd", label: "Min GGR (USD)", mono: true },
          { key: "payment_method", label: "Payout", render: (r) => <Badge>{r.payment_method}</Badge> },
          { key: "requires_db_verification", label: "DB Verify", render: (r) => r.requires_db_verification ? <Badge tone="success">Yes</Badge> : <Badge tone="warning">No</Badge> },
        ]}
        rows={rows}
        actions={isAdmin ? (r) => (
          <div className="flex gap-2">
            <button data-testid={`provider-edit-${r.code}`} onClick={() => { setEditing(r); setForm({ ...emptyForm, ...r, aggregator_id: r.aggregator_id || "" }); setOpen(true); }} className="text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]"><Icons.Pencil size={16}/></button>
            <button data-testid={`provider-del-${r.code}`} onClick={() => del(r.id)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><Icons.Trash2 size={16}/></button>
          </div>
        ) : null}
      />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit provider" : "New provider"}
        footer={<>
          <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="provider-save" onClick={save}>Save</Btn>
        </>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code"><TextInput data-testid="provider-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
          <Field label="Name"><TextInput data-testid="provider-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Aggregator">
            <Select data-testid="provider-aggregator" value={form.aggregator_id} onChange={(e) => setForm({ ...form, aggregator_id: e.target.value })}>
              <option value="">— none —</option>
              {aggs.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </Select>
          </Field>
          <Field label="Default GGR %"><TextInput type="number" step="0.01" value={form.default_ggr_pct} onChange={(e) => setForm({ ...form, default_ggr_pct: parseFloat(e.target.value) })} /></Field>
          <Field label="Minimum GGR (USD)"><TextInput type="number" step="0.01" value={form.min_ggr_usd} onChange={(e) => setForm({ ...form, min_ggr_usd: parseFloat(e.target.value) })} /></Field>
          <Field label="Invoice currency">
            <Select data-testid="provider-invoice-currency" value={form.invoice_currency || "USD"} onChange={(e) => setForm({ ...form, invoice_currency: e.target.value })}>
              {currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              {!currencies.find((c) => c.code === "USD") && <option value="USD">USD</option>}
              {!currencies.find((c) => c.code === "EUR") && <option value="EUR">EUR</option>}
            </Select>
          </Field>
          <Field label="Payment method">
            <Select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
              <option value="USDT_TRC20">USDT TRC20</option>
              <option value="BTC">BTC</option>
              <option value="BANK">Bank Transfer</option>
            </Select>
          </Field>
          <div className="col-span-2 flex items-center gap-2">
            <input id="db_verify" type="checkbox" checked={form.requires_db_verification} onChange={(e) => setForm({ ...form, requires_db_verification: e.target.checked })} />
            <label htmlFor="db_verify" className="text-sm text-[var(--wv-text-2)]">Requires DB verification (some providers with minimum GGR can skip)</label>
          </div>
          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <div className="wv-overline">Per-game GGR overrides</div>
              <button type="button" onClick={addOverride} className="text-sm text-[var(--wv-primary)] hover:underline flex items-center gap-1">
                <Plus size={14}/> Add game
              </button>
            </div>
            <div className="space-y-2">
              {form.game_overrides.map((g, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <TextInput placeholder="Game name" value={g.game} onChange={(e) => setOverride(i, "game", e.target.value)} />
                  <TextInput type="number" step="0.01" placeholder="GGR %" value={g.ggr_pct} onChange={(e) => setOverride(i, "ggr_pct", parseFloat(e.target.value))} className="w-32"/>
                  <button onClick={() => rmOverride(i)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><X size={16}/></button>
                </div>
              ))}
              {form.game_overrides.length === 0 && <div className="text-sm text-[var(--wv-text-3)]">No overrides — default GGR% applies to all games.</div>}
            </div>
          </div>
          <div className="col-span-2"><Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></Field></div>
        </div>
      </Modal>
    </AppShell>
  );
}
